/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import React from 'react';

interface FilterSelectWithLabelProps<T> {
  disabled?: boolean;
  selectLabel: string;
  optionList: T[];
  selectedItem: T | null;
  handleSelectChange: (item: T) => void;
  getLabel: (item: T) => string;
  getValue: (item: T) => string | number;
}

const FilterSelectWithLabel = <T,>({
  disabled,
  selectLabel,
  optionList,
  selectedItem,
  handleSelectChange,
  getLabel,
  getValue,
}: FilterSelectWithLabelProps<T>) => {
  return (
    <FormControl fullWidth>
      <InputLabel shrink variant="standard" id="demo-simple-select-label">
        {selectLabel}
      </InputLabel>
      <Select
        disabled={disabled}
        displayEmpty
        labelId="demo-simple-select-label"
        placeholder={selectLabel}
        label={selectLabel}
        variant="standard"
        value={selectedItem ? getValue(selectedItem) : ''}
        onChange={(e) => {
          const selectedValue = e.target.value;
          const selectedItem = optionList?.find(
            (item) => getValue(item) === selectedValue,
          );
          if (selectedItem) handleSelectChange(selectedItem);
        }}
      >
        {optionList?.map((option) => (
          <MenuItem key={getValue(option)} value={getValue(option)}>
            {getLabel(option)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default FilterSelectWithLabel;
